import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Package, Star, Wine, MapPin, Check, ArrowUpRight, Clock, MagnifyingGlass, Storefront } from '@phosphor-icons/react';
import { useCart } from '../../context/CartContext';
import { searchClient } from '../../services/algoliaClient';
import { useStore } from '../../context/StoreContext';
import { getCategoryIcon } from '../../utils/categoryIcons';
import { useTranslation } from 'react-i18next';
import { getCategoryById } from '../../data/categories';
import { getRegionInfoUrl, getAppellationInfoUrl, getCountryInfoUrl } from '../../utils/grapeVarietyUtils';
import { InfoPill } from '../Product/InfoPill';
import { GrapeVarietyPill } from '../Product/GrapeVarietyPill';
import { RecommendWidget } from '../Product/RecommendWidget';
import { AddToCartStepper } from '../Product/AddToCartStepper';
import { insights } from '../../services/algoliaInsights';
import { recordEvent } from '../../utils/userProfile';

// Add a useMediaQuery hook for responsive detection
function useMediaQuery(query) {
  const [matches, setMatches] = useState(window.matchMedia(query).matches);
  useEffect(() => {
    const media = window.matchMedia(query);
    if (media.matches !== matches) {
      setMatches(media.matches);
    }
    const listener = () => setMatches(media.matches);
    media.addEventListener('change', listener);
    return () => media.removeEventListener('change', listener);
  }, [matches, query]);
  return matches;
}

// Product Detail Page
export const ProductDetailPage = () => {
  const { productId } = useParams();
  const [searchParams] = useState(new URLSearchParams(window.location.search));
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [storeAvailabilityModalOpen, setStoreAvailabilityModalOpen] = useState(false);
  const { selectedStore, storeResults } = useStore();
  const { setIsCartOpen } = useCart();
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 768px)');

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await searchClient.search([{
          indexName: 'saq_products',
          params: {
            filters: `objectID:${productId}`,
            hitsPerPage: 1
          }
        }]);
        
        if (response.results[0].hits.length > 0) {
          const productData = response.results[0].hits[0];
          setProduct(productData);
          
          // Note: View events are automatically sent by InstantSearch when products appear in search results
          // We don't need to manually send view events here
        }
      } catch (error) {
        console.error('Error fetching product:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [productId]);

  useEffect(() => {
    if (!product) return;
    recordEvent({
      type: 'view',
      product: { ...product, selectedStore: String(selectedStore || '1') },
    });
  }, [product, selectedStore]);

  const handleImageClick = () => {
    setImageModalOpen(true);
  };

  const handleModalClose = () => {
    setImageModalOpen(false);
  };

  const handleStoreAvailabilityModalClose = () => {
    setStoreAvailabilityModalOpen(false);
  };

  const handleSaqLinkClick = () => {
    // Track conversion event when user goes to SAQ
    const queryID = searchParams.get('queryID');
    
    if (queryID && product) {
      insights.convertedObjectIDsAfterSearch(
        'Went To Saq',
        'saq_products',
        [product.objectID],
        queryID,
        product.price || 0
      );
    } else if (product) {
      insights.convertedObjectIDs(
        'Went To Saq',
        'saq_products',
        [product.objectID],
        product.price || 0
      );
    }
  };

  // Helper function to get current day of week in French
  const getCurrentDay = () => {
    const now = new Date();
    const currentDay = now.getDay();
    const dayNames = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
    return dayNames[currentDay];
  };

  // Helper function to check if store is currently open
  const isStoreOpen = (store) => {
    if (!store || !store.opening_hours || store.opening_hours.length === 0) return false;
    
    const now = new Date();
    const currentDay = now.getDay();
    const currentTime = now.getHours() * 60 + now.getMinutes();
    
    const dayNames = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
    const todayName = dayNames[currentDay];
    
    const todayHours = store.opening_hours.find(day => day.day.toLowerCase() === todayName);
    if (!todayHours || !todayHours.hours) return false;
    
    const hoursMatch = todayHours.hours.match(/(\d{1,2})[h:](\d{2})-(\d{1,2})[h:](\d{2})/);
    if (!hoursMatch) return false;
    
    const openHour = parseInt(hoursMatch[1]);
    const openMinute = parseInt(hoursMatch[2]);
    const closeHour = parseInt(hoursMatch[3]);
    const closeMinute = parseInt(hoursMatch[4]);
    
    const openTime = openHour * 60 + openMinute;
    let closeTime = closeHour * 60 + closeMinute;
    
    if (closeTime < openTime) {
      closeTime += 24 * 60;
    }
    
    return currentTime >= openTime && currentTime <= closeTime;
  };

  // Helper function to get store hours for today
  const getStoreHours = (store) => {
    if (!store || !store.opening_hours) return null;
    
    const currentDay = getCurrentDay();
    const dayHours = store.opening_hours.find(hour => 
      hour.day.toLowerCase() === currentDay
    );
    
    if (!dayHours) return null;
    
    return dayHours.hours;
  };

  if (loading) {
    return (
      <div className="product-detail-loading" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <div className="loading-spinner" style={{ color: '#4B164C', fontSize: 18 }}>Chargement du produit...</div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="product-not-found" style={{ textAlign: 'center', padding: '50px 20px' }}>
        <h2 style={{ color: '#4B164C' }}>Produit non trouvé</h2>
        <p>Le produit que vous recherchez n'est pas disponible.</p>
        <button onClick={() => navigate('/')} style={{ background: '#4B164C', color: '#fff', border: 'none', padding: '12px 24px', borderRadius: 8, cursor: 'pointer' }}>Retour à l'accueil</button>
      </div>
    );
  }

  const IconComponent = getCategoryIcon(product.categorias_flat);
  const storeData = product.store_data?.[`store_${selectedStore?.padStart(3, '0')}`];
  const price = storeData?.price || product.price;

  // Extract product attributes
  const format = product.attributes?.format || '750 ml';
  const alcohol = product.attributes?.taux_alcool || '';
  const region = product.attributes?.region_origine || '';
  const grape = product.attributes?.cepage || '';
  const appellation = product.attributes?.appellation || '';
  const country = product.attributes?.pays_origine || '';
  const designation = product.attributes?.designation_reglementee || '';
  const saqCode = product.sku || product.objectID;

  // Get available stores
  const availableStores = product.attributes?.store_availability_list || [];
  const storeDetails = (storeResults || []).filter(store => availableStores.includes(store.store_id));

  // Check if product is available in selected store
  const isAvailableInSelectedStore = selectedStore && availableStores.includes(selectedStore);

  // Generate SAQ URL
  const saqUrl = `https://www.saq.com/fr/produits/vin/${saqCode}`;

  return (
    <>
      <main className="product-detail-page" style={{ 
        padding: isMobile ? '12px' : '24px', 
        maxWidth: 1200, 
        margin: '0 auto'
      }}>
        <div className="product-detail-container">
          <div className="product-detail-breadcrumb" style={{ 
            marginBottom: isMobile ? 16 : 24, 
            color: '#888',
            fontSize: isMobile ? '12px' : '14px'
          }}>
            <Link to="/" style={{ color: '#4B164C', textDecoration: 'none' }}>Accueil</Link>
            {product.categorias_flat?.[0] && (
              <>
                <span> / </span>
                {(() => {
                  // Find the category based on product identity
                  const category = getCategoryById(product.attributes?.identite_produit);
                  const categorySlug = category?.slug || 'categories';
                  return (
                    <Link to={`/categories/${categorySlug}`} style={{ color: '#4B164C', textDecoration: 'none' }}>
                      {product.categorias_flat[0]}
                    </Link>
                  );
                })()}
              </>
            )}
            <span> / </span>
            {!isMobile && <span>{product.name}</span>}
          </div>

          <div className="product-detail-content" style={{ 
            display: isMobile ? 'flex' : 'grid', 
            gridTemplateColumns: isMobile ? 'none' : '1fr 1fr', 
            gap: isMobile ? 24 : 48, 
            marginBottom: isMobile ? 24 : 48,
            flexDirection: isMobile ? 'column' : 'row'
          }}>
            <div className="product-detail-image" style={{ 
              textAlign: 'center',
              order: isMobile ? 1 : 'unset'
            }}>
              <div className="product-image-placeholder" style={{ 
                background: '#f8f6fa', 
                borderRadius: isMobile ? 12 : 18, 
                padding: isMobile ? 20 : 32, 
                boxShadow: '0 4px 24px rgba(75,22,76,0.10)', 
                position: 'relative'
              }}>
                {product.image ? (
                  <div style={{ position: 'relative', display: 'inline-block' }}>
                    <img 
                      src={product.image.startsWith('http') ? product.image : `https:${product.image}`} 
                      alt={product.name} 
                      style={{ 
                        maxWidth: '100%', 
                        maxHeight: isMobile ? '60vh' : 400, 
                        borderRadius: 12, 
                        objectFit: 'contain',
                        cursor: 'pointer',
                        transition: 'transform 0.2s ease',
                        width: isMobile ? 'auto' : '100%',
                        height: isMobile ? 'auto' : 'auto'
                      }}
                      onClick={handleImageClick}
                      onMouseOver={e => {
                        e.target.style.transform = 'scale(1.02)';
                      }}
                      onMouseOut={e => {
                        e.target.style.transform = 'scale(1)';
                      }}
                    />
                    {/* Zoom icon overlay */}
                    <div 
                      style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        background: 'rgba(75,22,76,0.8)',
                        borderRadius: '50%',
                        width: 48,
                        height: 48,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: 0,
                        transition: 'opacity 0.2s ease',
                        pointerEvents: 'none'
                      }}
                      className="zoom-icon"
                    >
                      <MagnifyingGlass size={24} color="#fff" weight="bold" />
                    </div>
                  </div>
                ) : (
                  <Wine size={200} color="#4B164C" />
                )}
              </div>
            </div>

            <div className="product-detail-info" style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: isMobile ? 16 : 24,
              order: isMobile ? 2 : 'unset'
            }}>
              <div className="product-header">
                <h1 style={{ 
                  fontSize: isMobile ? 24 : 28, 
                  color: '#4B164C', 
                  marginBottom: isMobile ? 6 : 8, 
                  lineHeight: 1.2
                }}>{product.name}</h1>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 12, 
                  marginBottom: isMobile ? 12 : 16
                }}>
                  {IconComponent && <IconComponent size={24} color="#4B164C" />}
                  <span style={{ 
                    color: '#888', 
                    fontSize: isMobile ? 14 : 16
                  }}>{product.categorias_flat?.[0]}</span>
                </div>
                <div style={{ 
                  fontSize: isMobile ? 28 : 32, 
                  fontWeight: 700, 
                  color: '#4B164C', 
                  marginBottom: isMobile ? 12 : 16
                }}>
                  {price ? `${price.toLocaleString('fr-CA', { minimumFractionDigits: 2 })} $` : ''}
                </div>
                
                {/* Available in Selected Store Pill */}
                {isAvailableInSelectedStore && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    background: '#e8f5e8',
                    color: '#388e3c',
                    padding: '8px 16px',
                    borderRadius: 20,
                    fontSize: 14,
                    fontWeight: 600,
                    border: '1px solid #c8e6c9',
                    marginBottom: isMobile ? 12 : 16,
                    width: 'fit-content'
                  }}>
                    <Check size={16} weight="bold" />
                    <span>Disponible dans ma succursale</span>
                  </div>
                )}
                
                {product.customer_rating && (
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 8, 
                    marginBottom: isMobile ? 12 : 16
                  }}>
                    <Star size={20} weight="fill" color="#4B164C" />
                    <span style={{ color: '#4B164C', fontWeight: 600 }}>{product.customer_rating}</span>
                  </div>
                )}
              </div>

              {/* Availability Section */}
              <div className="availability-section">
                <h3 style={{ fontSize: 18, color: '#4B164C', marginBottom: 12 }}>Disponibilité</h3>
                <div className="availability-container" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {product.attributes?.availability_front ? (
                    (() => {
                      const availabilityArray = Array.isArray(product.attributes.availability_front) 
                        ? product.attributes.availability_front 
                        : product.attributes.availability_front.split(/(?=[A-Z])/).filter(part => part.trim());
                      
                      const hasSoonAvailable = availabilityArray.some(type => 
                        type.toLowerCase().includes('bientôt') || type.toLowerCase().includes('soon')
                      );
                      
                      if (hasSoonAvailable) {
                        const soonType = availabilityArray.find(type => 
                          type.toLowerCase().includes('bientôt') || type.toLowerCase().includes('soon')
                        );
                        return (
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                            background: '#fff3e0',
                            color: '#f57c00',
                            padding: '6px 16px',
                            borderRadius: 20,
                            fontSize: 14,
                            fontWeight: 500,
                            border: '1px solid #ffcc80'
                          }}>
                            <Clock size={16} weight="bold" />
                            <span>{soonType.trim()}</span>
                          </div>
                        );
                      }
                      
                      return availabilityArray.map((availabilityType, index) => {
                        const isSoonAvailable = availabilityType.toLowerCase().includes('bientôt') || availabilityType.toLowerCase().includes('soon');
                        
                        return (
                          <div key={index} style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                            background: isSoonAvailable ? '#fff3e0' : '#e8f5e8',
                            color: isSoonAvailable ? '#f57c00' : '#388e3c',
                            padding: '6px 16px',
                            borderRadius: 20,
                            fontSize: 14,
                            fontWeight: 500,
                            border: `1px solid ${isSoonAvailable ? '#ffcc80' : '#c8e6c9'}`
                          }}>
                            {isSoonAvailable ? (
                              <Clock size={16} weight="bold" />
                            ) : (
                              <Check size={16} weight="bold" />
                            )}
                            <span>{availabilityType.trim()}</span>
                          </div>
                        );
                      });
                    })()
                  ) : null}
                </div>
              </div>

              {/* Store Availability Section */}
              {storeDetails.length > 0 && (
                <div className="store-availability-section">
                  <h3 style={{ fontSize: 18, color: '#4B164C', marginBottom: 12 }}>Disponibilité en succursales</h3>
                  <button
                    onClick={() => setStoreAvailabilityModalOpen(true)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      background: '#f8f6fa',
                      border: '2px solid #4B164C',
                      borderRadius: 12,
                      padding: '16px 20px',
                      color: '#4B164C',
                      fontSize: 16,
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      width: '100%',
                      justifyContent: 'space-between'
                    }}
                    onMouseOver={e => {
                      e.currentTarget.style.background = '#4B164C';
                      e.currentTarget.style.color = '#fff';
                    }}
                    onMouseOut={e => {
                      e.currentTarget.style.background = '#f8f6fa';
                      e.currentTarget.style.color = '#4B164C';
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <Storefront size={24} weight="bold" />
                      <div style={{ textAlign: 'left' }}>
                        <div>Voir les {storeDetails.length} succursales</div>
                        <div style={{ fontSize: 14, fontWeight: 400, opacity: 0.8 }}>
                          Disponible dans votre région
                        </div>
                      </div>
                    </div>
                    <ArrowUpRight size={20} />
                  </button>
                </div>
              )}

              {/* Action Buttons */}
              <div className="product-actions" style={{ 
                display: 'flex', 
                flexDirection: isMobile ? 'column' : 'row', 
                flexWrap: 'wrap', 
                gap: 12, 
                marginTop: 8, 
                alignItems: isMobile ? 'stretch' : 'center' 
              }}>
                <AddToCartStepper
                  product={product}
                  queryID={searchParams.get('queryID') || null}
                  compact={false}
                  onAdd={() => setIsCartOpen?.(true)}
                />
                <a href={saqUrl} target="_blank" rel="noopener noreferrer" 
                   onClick={handleSaqLinkClick}
                   style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  background: 'transparent',
                  color: '#4B164C',
                  border: '2px solid #4B164C',
                  padding: '12px 20px',
                  borderRadius: 12,
                  textDecoration: 'none',
                  fontWeight: 600,
                  fontSize: 15,
                  transition: 'all 0.2s'
                }}
                onMouseOver={e => { e.currentTarget.style.background = '#4B164C'; e.currentTarget.style.color = '#fff'; }}
                onMouseOut={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#4B164C'; }}
                >
                  Voir sur SAQ.com
                  <ArrowUpRight size={16} />
                </a>
              </div>
            </div>
          </div>

          {/* Detailed Information Section */}
          <div className="detailed-info-section" style={{ 
            background: '#f8f6fa', 
            borderRadius: 18, 
            padding: 32, 
            marginTop: 32,
            boxShadow: '0 4px 24px rgba(75,22,76,0.08)'
          }}>
            <h2 style={{ fontSize: 24, color: '#4B164C', marginBottom: 24, fontWeight: 600 }}>Infos détaillées</h2>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 24 }}>
              {/* Left Column */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {country && (
                  <div className="info-row">
                    <div style={{ fontSize: 14, color: '#888', marginBottom: 4, fontWeight: 500 }}>Pays</div>
                    <InfoPill 
                      value={country}
                      infoUrl={getCountryInfoUrl(country)}
                      style={{ fontSize: 16, fontWeight: 500 }}
                    />
                  </div>
                )}
                
                {region && (
                  <div className="info-row">
                    <div style={{ fontSize: 14, color: '#888', marginBottom: 4, fontWeight: 500 }}>Région</div>
                    <InfoPill 
                      value={region}
                      infoUrl={getRegionInfoUrl(region, country)}
                      style={{ fontSize: 16, fontWeight: 500 }}
                    />
                  </div>
                )}
                
                {appellation && (
                  <div className="info-row">
                    <div style={{ fontSize: 14, color: '#888', marginBottom: 4, fontWeight: 500 }}>Appellation d'origine</div>
                    <InfoPill 
                      value={appellation}
                      infoUrl={getAppellationInfoUrl(appellation, region, country)}
                      style={{ fontSize: 16, fontWeight: 500 }}
                    />
                  </div>
                )}
                
                {designation && (
                  <div className="info-row">
                    <div style={{ fontSize: 14, color: '#888', marginBottom: 4, fontWeight: 500 }}>Désignation réglementée</div>
                    <div style={{ fontSize: 16, color: '#4B164C', fontWeight: 500 }}>{designation}</div>
                  </div>
                )}
                
                {grape && (
                  <div className="info-row">
                    <div style={{ fontSize: 14, color: '#888', marginBottom: 4, fontWeight: 500 }}>Cépage</div>
                    <GrapeVarietyPill 
                      grape={grape}
                      country={country}
                      region={region}
                      appellation={appellation}
                      style={{ fontSize: 16, fontWeight: 500 }}
                    />
                  </div>
                )}
                
                {alcohol && (
                  <div className="info-row">
                    <div style={{ fontSize: 14, color: '#888', marginBottom: 4, fontWeight: 500 }}>Degré d'alcool</div>
                    <div style={{ fontSize: 16, color: '#4B164C', fontWeight: 500 }}>{alcohol} %</div>
                  </div>
                )}
              </div>
              
              {/* Right Column */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {product.attributes?.taux_sucre && (
                  <div className="info-row">
                    <div style={{ fontSize: 14, color: '#888', marginBottom: 4, fontWeight: 500 }}>Taux de sucre</div>
                    <div style={{ fontSize: 16, color: '#4B164C', fontWeight: 500 }}>{product.attributes.taux_sucre} g/L</div>
                  </div>
                )}
                
                {product.color && (
                  <div className="info-row">
                    <div style={{ fontSize: 14, color: '#888', marginBottom: 4, fontWeight: 500 }}>Couleur</div>
                    <div style={{ fontSize: 16, color: '#4B164C', fontWeight: 500 }}>{product.color}</div>
                  </div>
                )}
                
                {format && (
                  <div className="info-row">
                    <div style={{ fontSize: 14, color: '#888', marginBottom: 4, fontWeight: 500 }}>Format</div>
                    <div style={{ fontSize: 16, color: '#4B164C', fontWeight: 500 }}>{format}</div>
                  </div>
                )}
                
                {product.attributes?.producteur && (
                  <div className="info-row">
                    <div style={{ fontSize: 14, color: '#888', marginBottom: 4, fontWeight: 500 }}>Producteur</div>
                    <div style={{ fontSize: 16, color: '#4B164C', fontWeight: 500 }}>{product.attributes.producteur}</div>
                  </div>
                )}
                
                {product.attributes?.agent_promotionnel && (
                  <div className="info-row">
                    <div style={{ fontSize: 14, color: '#888', marginBottom: 4, fontWeight: 500 }}>Agent promotionnel</div>
                    <div style={{ fontSize: 16, color: '#4B164C', fontWeight: 500 }}>{product.attributes.agent_promotionnel}</div>
                  </div>
                )}
                
                {saqCode && (
                  <div className="info-row">
                    <div style={{ fontSize: 14, color: '#888', marginBottom: 4, fontWeight: 500 }}>Code SAQ</div>
                    <div style={{ fontSize: 16, color: '#4B164C', fontWeight: 500 }}>{saqCode}</div>
                  </div>
                )}
                
                {product.attributes?.code_cup && (
                  <div className="info-row">
                    <div style={{ fontSize: 14, color: '#888', marginBottom: 4, fontWeight: 500 }}>Code CUP</div>
                    <div style={{ fontSize: 16, color: '#4B164C', fontWeight: 500 }}>{product.attributes.code_cup}</div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Recommendations Section */}
          <RecommendWidget 
            objectID={productId}
            title="Vous pourriez aussi aimer"
            maxRecommendations={4}
          />
        </div>
      </main>

      {/* Image Modal */}
      {imageModalOpen && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            cursor: 'pointer'
          }}
          onClick={handleModalClose}
        >
          <div 
            style={{
              position: 'relative',
              maxWidth: '90%',
              maxHeight: '90%',
              background: '#fff',
              borderRadius: 16,
              padding: 24,
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
            }}
            onClick={e => e.stopPropagation()}
          >
            <button
              onClick={handleModalClose}
              style={{
                position: 'absolute',
                top: 12,
                right: 12,
                background: 'rgba(0,0,0,0.5)',
                border: 'none',
                borderRadius: '50%',
                width: 32,
                height: 32,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: '#fff',
                fontSize: 18,
                zIndex: 1
              }}
            >
              ×
            </button>
            {product.image ? (
              <img 
                src={product.image.startsWith('http') ? product.image : `https:${product.image}`} 
                alt={product.name} 
                style={{ 
                  maxWidth: '100%', 
                  maxHeight: '70vh', 
                  objectFit: 'contain',
                  borderRadius: 8
                }}
              />
            ) : (
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                width: 400, 
                height: 500,
                background: '#f8f6fa',
                borderRadius: 8
              }}>
                <Wine size={150} color="#4B164C" />
              </div>
            )}
            <div style={{ 
              marginTop: 16, 
              textAlign: 'center',
              color: '#4B164C',
              fontWeight: 600,
              fontSize: 18
            }}>
              {product.name}
            </div>
          </div>
        </div>
      )}

      {/* Store Availability Modal/Slider */}
      {storeAvailabilityModalOpen && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: isMobile ? 'flex-end' : 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: isMobile ? 0 : '20px'
          }}
          onClick={handleStoreAvailabilityModalClose}
        >
          <div 
            style={{
              background: '#fff',
              borderRadius: isMobile ? '20px 20px 0 0' : 20,
              padding: isMobile ? 24 : 32,
              maxWidth: isMobile ? '100%' : 600,
              width: isMobile ? '100%' : '90%',
              maxHeight: isMobile ? '80vh' : '80vh',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 16px 48px rgba(75,22,76,0.20)'
            }}
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between', 
              marginBottom: 24,
              paddingBottom: 16,
              borderBottom: '1px solid #f0f0f0'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <Storefront size={24} color="#4B164C" />
                <h3 style={{ color: '#4B164C', fontSize: 20, fontWeight: 700, margin: 0 }}>
                  Disponibilité en succursales
                </h3>
              </div>
              <button 
                onClick={handleStoreAvailabilityModalClose} 
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  fontSize: 24, 
                  color: '#888', 
                  cursor: 'pointer',
                  padding: 4,
                  borderRadius: 8,
                  transition: 'background 0.2s'
                }}
                onMouseOver={e => e.currentTarget.style.background = '#f0f0f0'}
                onMouseOut={e => e.currentTarget.style.background = 'transparent'}
              >
                ×
              </button>
            </div>

            {/* Store List */}
            <div style={{ 
              flex: 1, 
              overflowY: 'auto',
              paddingRight: 8
            }}>
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: 12 
              }}>
                {storeDetails.map((store, index) => {
                  const storeHours = getStoreHours(store);
                  const storeIsOpen = isStoreOpen(store);
                  
                  return (
                    <div key={index} style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 16,
                      padding: 16,
                      background: '#faf9fb',
                      borderRadius: 12,
                      border: '1px solid #e0e0e0',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseOver={e => {
                      e.currentTarget.style.borderColor = '#4B164C';
                      e.currentTarget.style.background = '#f8f6fa';
                    }}
                    onMouseOut={e => {
                      e.currentTarget.style.borderColor = '#e0e0e0';
                      e.currentTarget.style.background = '#faf9fb';
                    }}
                    >
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        width: 48,
                        height: 48,
                        borderRadius: 12,
                        background: storeIsOpen ? '#e8f5e8' : '#ffebee',
                        color: storeIsOpen ? '#388e3c' : '#d32f2f',
                        flexShrink: 0
                      }}>
                        <Storefront size={24} weight="bold" />
                      </div>
                      
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 8, 
                          marginBottom: 8 
                        }}>
                          <h4 style={{ 
                            color: '#4B164C', 
                            fontSize: 16, 
                            fontWeight: 600, 
                            margin: 0 
                          }}>
                            {store.name}
                          </h4>
                          <div style={{
                            background: storeIsOpen ? '#388e3c' : '#d32f2f',
                            color: '#fff',
                            padding: '2px 8px',
                            borderRadius: 12,
                            fontSize: 11,
                            fontWeight: 600
                          }}>
                            {storeIsOpen ? 'OUVERT' : 'FERMÉ'}
                          </div>
                        </div>
                        
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 8, 
                          marginBottom: 8,
                          fontSize: 14,
                          color: '#666'
                        }}>
                          <MapPin size={14} />
                          <span>
                            {store.address?.street}{store.address?.street ? ', ' : ''}
                            {store.address?.city}{store.address?.city ? ', ' : ''}
                            {store.address?.region}
                          </span>
                        </div>
                        
                        {storeHours && (
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 6,
                            fontSize: 13,
                            color: storeIsOpen ? '#388e3c' : '#d32f2f',
                            fontWeight: 500
                          }}>
                            <Clock size={12} weight="bold" />
                            <span>{storeHours}</span>
                          </div>
                        )}
                        
                        {store.phone && (
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 6,
                            fontSize: 13,
                            color: '#666',
                            marginTop: 4
                          }}>
                            <Package size={12} />
                            <span>{store.phone}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Footer */}
            <div style={{ 
              marginTop: 24,
              paddingTop: 16,
              borderTop: '1px solid #f0f0f0',
              textAlign: 'center'
            }}>
              <div style={{ 
                fontSize: 14, 
                color: '#666', 
                marginBottom: 8 
              }}>
                {storeDetails.length} succursale{storeDetails.length > 1 ? 's' : ''} disponible{storeDetails.length > 1 ? 's' : ''}
              </div>
              <button
                onClick={handleStoreAvailabilityModalClose}
                style={{
                  background: '#4B164C',
                  color: '#fff',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: 8,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'background 0.2s'
                }}
                onMouseOver={e => e.currentTarget.style.background = '#3a1240'}
                onMouseOut={e => e.currentTarget.style.background = '#4B164C'}
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .product-image-placeholder:hover .zoom-icon {
          opacity: 1 !important;
        }
      `}</style>
    </>
  );
}; 